import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, Paper, Alert, CircularProgress,
  Fade, Chip, Divider, Stepper, Step, StepLabel, StepConnector,
  InputAdornment, IconButton, Tooltip, LinearProgress,
} from '@mui/material';
import {
  Coins, Search, Sparkles, Check, Globe, Rocket,
  ArrowRight, ArrowLeft, RefreshCw, Copy, Server, Mail, MailCheck,
  CheckCircle2, Smartphone, Shield,
  Clock, AlertTriangle, Wifi, WifiOff, ExternalLink, Settings
} from 'lucide-react';
import { styled } from '@mui/material/styles';
import { stepConnectorClasses } from '@mui/material/StepConnector';
import axios from 'axios';
import { getNomeRegistroDns, getDominioRaiz } from '../utils/domainUtils';

const SectionCard = styled(Paper)(() => ({
  background: '#141926',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 16,
  padding: 24,
  marginBottom: 24,
}));

const InstructionBox = styled(Box)(() => ({
  background: 'rgba(124,58,237,0.06)',
  border: '1px solid rgba(124,58,237,0.15)',
  borderRadius: 12,
  padding: 20,
}));

const FtmConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: { top: 18 },
  [`&.${stepConnectorClasses.active}`]: { [`& .${stepConnectorClasses.line}`]: { borderColor: '#7C3AED' } },
  [`&.${stepConnectorClasses.completed}`]: { [`& .${stepConnectorClasses.line}`]: { borderColor: '#10B981' } },
  [`& .${stepConnectorClasses.line}`]: { borderColor: 'rgba(107,114,128,0.3)', borderTopWidth: 2 },
}));

const FtmStepIcon = ({ active, completed, icon }) => (
  <Box sx={{
    width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.85rem', fontWeight: 700,
    background: completed ? '#10B981' : active ? '#7C3AED' : 'rgba(107,114,128,0.2)',
    color: '#fff', transition: 'all 0.3s',
  }}>
    {completed ? <Check size={16} /> : icon}
  </Box>
);

const SERVER_IP = '187.127.21.250';
const IA_TOKEN_COST = 1;
const API = process.env.REACT_APP_API_URL;

const formatDomain = (input) => {
  return input
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-').replace(/^-+|-+$/g, '').substring(0, 30);
};

const formatCNPJ = (v) => {
  const n = v.replace(/\D/g, '').substring(0, 14);
  if (n.length <= 2) return n;
  if (n.length <= 5) return `${n.slice(0, 2)}.${n.slice(2)}`;
  if (n.length <= 8) return `${n.slice(0, 2)}.${n.slice(2, 5)}.${n.slice(5)}`;
  if (n.length <= 12) return `${n.slice(0, 2)}.${n.slice(2, 5)}.${n.slice(5, 8)}/${n.slice(8)}`;
  return `${n.slice(0, 2)}.${n.slice(2, 5)}.${n.slice(5, 8)}/${n.slice(8, 12)}-${n.slice(12)}`;
};

const getUserData = () => {
  try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
};

export default function FtmLegacy() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pendingSite, setPendingSite] = useState(null);

  const [userTokens, setUserTokens] = useState(getUserData().tokens || 0);

  // Step 1: CNPJ
  const [cnpjInput, setCnpjInput] = useState('');
  const [cnpjLoading, setCnpjLoading] = useState(false);
  const [cnpjData, setCnpjData] = useState(null);
  const [telefone, setTelefone] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [email, setEmail] = useState('');
  const [dominio, setDominio] = useState('');

  // Step 2: AI generation
  const [iaLoading, setIaLoading] = useState(false);
  const [, setTextos] = useState({ missao: '', sobre: '', rodape: '' });
  const [createdSite, setCreatedSite] = useState(null);
  const [creating, setCreating] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('padrao');

  // Step 3: Domain + DNS
  const [customDomain, setCustomDomain] = useState('');
  const [dnsStatus, setDnsStatus] = useState(null);
  const [dnsChecking, setDnsChecking] = useState(false);
  const [dnsCheckCount, setDnsCheckCount] = useState(0);
  const [activating, setActivating] = useState(false);
  const [autoCheckEnabled, setAutoCheckEnabled] = useState(false);
  const [resumeDnsTrigger, setResumeDnsTrigger] = useState(false);
  const autoCheckRef = useRef(null);

  // Step 4: MX
  const [mxStatus, setMxStatus] = useState(null);
  const [mxChecking, setMxChecking] = useState(false);
  const [settingUpEmail, setSettingUpEmail] = useState(false);

  const dominioAtual = createdSite?.customDomain || customDomain;
  const nomeRegistro = getNomeRegistroDns(dominioAtual);
  const ehSubdominio = nomeRegistro !== '@';

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copied, setCopied] = useState('');

  const authHeader = useCallback(() => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  }, []);

  const updateWizardStep = useCallback(async (siteId, step, extraData) => {
    try {
      await axios.put(`${API}/api/sites/wizard/${siteId}`, {
        wizardStep: step,
        wizardData: extraData || undefined,
      }, authHeader());
    } catch { /* best-effort */ }
  }, [authHeader]);

  // On mount: check for pending wizard in the database
  useEffect(() => {
    const checkPending = async () => {
      try {
        const res = await axios.get(`${API}/api/sites/wizard/pending`, { ...authHeader(), timeout: 8000 });
        if (res.data.hasPending && res.data.site) {
          setPendingSite(res.data.site);
        }
      } catch { /* no pending */ }
      setLoading(false);
    };
    checkPending();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResume = () => {
    if (!pendingSite) return;
    const site = pendingSite;
    const wd = site.wizardData || {};

    setCreatedSite({ _id: site._id, name: site.name, customDomain: site.customDomain || null, emailEnabled: site.emailEnabled || false });
    if (wd.cnpjData) setCnpjData(wd.cnpjData);
    if (wd.telefone) setTelefone(wd.telefone);
    if (wd.whatsapp) setWhatsapp(wd.whatsapp);
    if (wd.email) setEmail(wd.email);
    if (wd.dominio) setDominio(wd.dominio || site.name);
    if (wd.customDomain) setCustomDomain(wd.customDomain);
    if (site.template) setSelectedTemplate(site.template);

    let step = site.wizardStep || 3;
    if (site.customDomain && !site.emailEnabled) step = 4;
    else if (!site.customDomain) step = 3;

    setActiveStep(step);
    setPendingSite(null);
    if (wd.customDomain && !site.customDomain) setResumeDnsTrigger(true);
  };

  const handleStartNew = async () => {
    const abandonId = pendingSite?._id;
    if (abandonId) {
      try {
        await axios.post(`${API}/api/sites/wizard/${abandonId}/abandon`, {}, authHeader());
      } catch (e) {
        console.error('[FtmLegacy] falha ao abandonar wizard pendente', { abandonId, status: e?.response?.status, msg: e?.message });
      }
    }
    setPendingSite(null);
    setCreatedSite(null);
    setCnpjData(null);
    setWhatsapp('');
    setCustomDomain('');
    setDnsStatus(null);
    setDnsChecking(false);
    setDnsCheckCount(0);
    setAutoCheckEnabled(false);
    if (autoCheckRef.current) { clearInterval(autoCheckRef.current); autoCheckRef.current = null; }
    setSelectedTemplate('padrao');
    setActiveStep(0);
  };

  useEffect(() => {
    setUserTokens(getUserData().tokens || 0);
    setError('');
    setSuccess('');
    if (activeStep !== 3 && autoCheckRef.current) {
      clearInterval(autoCheckRef.current);
      autoCheckRef.current = null;
      setAutoCheckEnabled(false);
    }

    // Sincroniza saldo real ao entrar no step de checagem ou de geração de IA
    if (activeStep === 0 || activeStep === 2) {
      (async () => {
        try {
          const meRes = await axios.get(`${API}/api/auth/me`, { ...authHeader(), timeout: 8000 });
          if (meRes.data?.success && meRes.data.user) {
            const tokens = meRes.data.user.tokens || 0;
            const userLs = getUserData();
            userLs.tokens = tokens;
            localStorage.setItem('user', JSON.stringify(userLs));
            setUserTokens(tokens);
          }
        } catch { /* mantém o valor local */ }
      })();
    }
  }, [activeStep, authHeader]);

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const steps = ['Tokens', 'CNPJ', 'Gerar Site', 'Domínio + DNS', 'Email', 'Concluído'];

  // --- Step 0: Check tokens ---
  const handleTokenCheck = async () => {
    let tokens = getUserData().tokens || 0;
    try {
      const meRes = await axios.get(`${API}/api/auth/me`, { ...authHeader(), timeout: 8000 });
      if (meRes.data?.success && meRes.data.user) {
        tokens = meRes.data.user.tokens || 0;
        const userLs = getUserData();
        userLs.tokens = tokens;
        localStorage.setItem('user', JSON.stringify(userLs));
      }
    } catch { /* fallback ao localStorage */ }

    setUserTokens(tokens);
    if (tokens >= IA_TOKEN_COST) {
      setActiveStep(1);
    } else {
      setError(`Você precisa de pelo menos ${IA_TOKEN_COST} tokens para continuar.`);
    }
  };

  // --- Step 1: CNPJ ---
  const handleBuscarCNPJ = async () => {
    const cnpjLimpo = cnpjInput.replace(/\D/g, '');
    if (cnpjLimpo.length !== 14) { setError('CNPJ deve ter 14 dígitos'); return; }
    setError(''); setCnpjLoading(true);
    try {
      const res = await axios.get(`${API}/api/sites/cnpj/${cnpjLimpo}`, { ...authHeader(), timeout: 15000 });
      if (res.data.success) {
        const d = res.data.data;
        setCnpjData(d);
        setDominio(formatDomain(d.razao_social || d.nome_fantasia || 'meusite'));
        if (d.telefone1) {
          const tel = d.ddd_telefone1 || d.telefone1 || '';
          setTelefone(tel);
          setWhatsapp(prev => prev || tel);
        }
        if (d.email) setEmail(d.email || '');
      } else { setError(res.data.message || 'CNPJ não encontrado'); }
    } catch (err) { setError(err.response?.data?.message || 'Erro ao buscar CNPJ'); }
    finally { setCnpjLoading(false); }
  };

  // --- Step 2: Generate AI + Create Site ---
  const handleGerarECriar = async () => {
    if (!dominio.trim()) { setError('Informe o domínio do site'); return; }
    if (!telefone.trim() || !email.trim()) { setError('Telefone e Email são obrigatórios'); return; }
    setError(''); setIaLoading(true);
    try {
      // Revalida saldo de tokens contra o servidor antes de disparar a IA.
      // localStorage pode estar desatualizado (usuário gastou em outra aba, etc).
      let tokensAtuais = userTokens;
      try {
        const meRes = await axios.get(`${API}/api/auth/me`, { ...authHeader(), timeout: 8000 });
        if (meRes.data?.success && meRes.data.user) {
          tokensAtuais = meRes.data.user.tokens || 0;
          const userLs = getUserData();
          userLs.tokens = tokensAtuais;
          localStorage.setItem('user', JSON.stringify(userLs));
          setUserTokens(tokensAtuais);
        }
      } catch { /* segue com o valor local; backend ainda protege */ }

      if (tokensAtuais < IA_TOKEN_COST) {
        setIaLoading(false);
        setError(`Você precisa de ${IA_TOKEN_COST} token (R$ 40,00) para gerar o site. Saldo atual: ${tokensAtuais}.`);
        setActiveStep(0);
        return;
      }

      const d = cnpjData;
      const endereco = [d.logradouro, d.numero, d.bairro, `${d.municipio}-${d.uf}`, `CEP ${d.cep}`].filter(Boolean).join(', ');
      const cnaePrincipal = d.cnae_fiscal_descricao ? { codigo: String(d.cnae_fiscal), descricao: d.cnae_fiscal_descricao } : null;
      const cnaesSecundarios = (d.cnaes_secundarios || []).map(c => ({ codigo: String(c.codigo), descricao: c.descricao }));
      const situacaoMap = { 1: 'Nula', 2: 'Ativa', 3: 'Suspensa', 4: 'Inapta', 8: 'Baixada' };

      const iaRes = await axios.post(
        `${API}/api/sites/gerar-textos-ia`,
        {
          razao_social: d.razao_social,
          nome_fantasia: d.nome_fantasia || d.razao_social,
          cnpj: formatCNPJ(String(d.cnpj)),
          data_abertura: d.data_inicio_atividade ? new Date(d.data_inicio_atividade).toLocaleDateString('pt-BR') : 'N/A',
          porte: d.porte || d.descricao_porte || 'N/A',
          natureza_juridica: d.natureza_juridica || d.descricao_natureza_juridica || 'N/A',
          capital_social: (d.capital_social || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 }),
          situacao_cadastral: situacaoMap[d.situacao_cadastral] || d.descricao_situacao_cadastral || 'Ativa',
          endereco,
          cnae_principal: cnaePrincipal,
          cnaes_secundarios: cnaesSecundarios,
          email: email.trim(),
          telefone: telefone.trim(),
        },
        { ...authHeader(), timeout: 60000 }
      );

      if (!iaRes.data.success) { setError(iaRes.data.message || 'Erro ao gerar textos'); setIaLoading(false); return; }

      setTextos({ missao: iaRes.data.missao, sobre: iaRes.data.sobre, rodape: iaRes.data.rodape });

      const user = getUserData();
      user.tokens = iaRes.data.tokensRestantes ?? (user.tokens - IA_TOKEN_COST);
      localStorage.setItem('user', JSON.stringify(user));
      setUserTokens(user.tokens);

      setCreating(true);
      const finalDominio = dominio.trim() || formatDomain(d.razao_social || d.nome_fantasia || 'meusite');
      const config = {
        DOMINIO: finalDominio,
        'RAZÃO': d.razao_social,
        CNPJ: formatCNPJ(String(d.cnpj)),
        'MISSÃO': iaRes.data.missao.replace(/\n/g, '<br>'),
        SOBRE: iaRes.data.sobre.replace(/\n/g, '<br>'),
        RODAPE: iaRes.data.rodape.replace(/\n/g, '<br>'),
        TELEFONE: telefone.trim(),
        EMAIL: email.trim(),
        INSTAGRAM: '',
        WHATSAPP: whatsapp.trim() || telefone.trim(),
        LINK_PAGINA: '',
        PIXEL_META: '',
        APP_ID: '',
        META_TAG: '',
      };

      const wizardData = {
        cnpjData: d,
        telefone: telefone.trim(),
        whatsapp: whatsapp.trim() || telefone.trim(),
        email: email.trim(),
        dominio: finalDominio,
      };

      const siteRes = await axios.post(
        `${API}/api/sites/generate`,
        { config, iaGenerated: true, template: selectedTemplate, wizardData },
        { ...authHeader(), timeout: 60000 }
      );

      if (siteRes.data.success) {
        const siteId = siteRes.data.siteId || siteRes.data.siteData?._id;
        setCreatedSite({ _id: siteId, name: finalDominio });
        setSuccess('Site gerado com sucesso!');
        setActiveStep(3);
      } else { setError(siteRes.data.message || 'Erro ao criar site'); }
    } catch (err) { setError(err.response?.data?.message || 'Erro ao gerar site'); }
    finally { setIaLoading(false); setCreating(false); }
  };

  const domainRegex = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*\.[a-z]{2,}$/;
  const isCustomDomainValid = customDomain.trim() && domainRegex.test(customDomain.trim());

  const handleCheckDns = useCallback(async (silent = false) => {
    if (!silent) { setError(''); setDnsChecking(true); setDnsStatus(null); }
    try {
      const res = await axios.get(
        `${API}/api/sites/${createdSite._id}/check-dns`,
        { params: { domain: customDomain.trim() }, ...authHeader() }
      );
      setDnsStatus(res.data);
      setDnsCheckCount(prev => prev + 1);
      if (res.data.canActivate && autoCheckRef.current) {
        clearInterval(autoCheckRef.current);
        autoCheckRef.current = null;
        setAutoCheckEnabled(false);
      }
    } catch { if (!silent) setError('Erro ao verificar DNS'); }
    finally { if (!silent) setDnsChecking(false); }
  }, [createdSite, customDomain, authHeader]);

  const toggleAutoCheck = useCallback(() => {
    if (autoCheckEnabled) {
      if (autoCheckRef.current) clearInterval(autoCheckRef.current);
      autoCheckRef.current = null;
      setAutoCheckEnabled(false);
    } else {
      setAutoCheckEnabled(true);
      handleCheckDns(false);
      autoCheckRef.current = setInterval(() => handleCheckDns(true), 30000);
    }
  }, [autoCheckEnabled, handleCheckDns]);

  useEffect(() => {
    return () => {
      if (autoCheckRef.current) clearInterval(autoCheckRef.current);
    };
  }, []);

  // R-002: ao retomar wizard no step 3 com domínio configurado, restaurar dnsStatus uma vez
  useEffect(() => {
    if (resumeDnsTrigger && createdSite?._id && customDomain && !autoCheckEnabled) {
      handleCheckDns(false);
      setResumeDnsTrigger(false);
    }
  }, [resumeDnsTrigger, createdSite, customDomain, autoCheckEnabled, handleCheckDns]);

  const handleSaveCustomDomain = async () => {
    if (!createdSite?._id || !customDomain.trim()) return;
    await updateWizardStep(createdSite._id, 3, { customDomain: customDomain.trim() });
  };

  const handleActivateDomain = async () => {
    setError(''); setActivating(true);
    try {
      const res = await axios.post(
        `${API}/api/sites/${createdSite._id}/custom-domain`,
        { domain: customDomain.trim() },
        { ...authHeader(), timeout: 300000 }
      );
      if (res.data.success) {
        setSuccess('Domínio ativado com SSL!');
        setCreatedSite(prev => ({ ...prev, customDomain: customDomain.trim().replace(/^www\./, '') }));
        setActiveStep(4);
      } else {
        setDnsStatus(null);
        setDnsCheckCount(0);
        setError(res.data.message);
      }
    } catch (err) {
      setDnsStatus(null);
      setDnsCheckCount(0);
      setError(err.response?.data?.message || 'Erro ao ativar domínio');
    }
    finally { setActivating(false); }
  };

  const handleCheckMx = async () => {
    setError(''); setMxChecking(true); setMxStatus(null);
    try {
      const res = await axios.get(`${API}/api/sites/${createdSite._id}/check-mx`, authHeader());
      setMxStatus(res.data);
    } catch { setError('Erro ao verificar MX'); }
    finally { setMxChecking(false); }
  };

  const handleSetupEmail = async () => {
    setError(''); setSettingUpEmail(true);
    try {
      const res = await axios.post(`${API}/api/sites/${createdSite._id}/setup-email`, {}, authHeader());
      if (res.data.success) {
        setSuccess('Email ativado com sucesso!');
        setActiveStep(5);
      } else { setError(res.data.message); }
    } catch (err) { setError(err.response?.data?.message || 'Erro ao configurar email'); }
    finally { setSettingUpEmail(false); }
  };

  const siteName = createdSite?.name || '';
  const siteEmail = createdSite?.customDomain
    ? `contato@${createdSite.customDomain}`
    : `${siteName}@ftmpainel.com.br`;

  const isFinalStep = activeStep === 5;

  return (
    <Fade in timeout={400}>
      <Box sx={{ maxWidth: 780, mx: 'auto' }}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Shield size={22} color="#7C3AED" />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#F9FAFB', letterSpacing: '-0.02em' }}>
              FTM Legacy
            </Typography>
            <Chip label="NOVO" size="small" sx={{ background: 'rgba(16,185,129,0.15)', color: '#10B981', fontWeight: 700, fontSize: '0.7rem', height: 22 }} />
          </Box>
          <Typography variant="body2" sx={{ color: '#6B7280' }}>
            Do zero à verificação de BM — passo a passo guiado
          </Typography>
        </Box>

        {/* Loading */}
        {loading && (
          <SectionCard elevation={0}>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={32} sx={{ color: '#7C3AED', mb: 2 }} />
              <Typography sx={{ color: '#D1D5DB', fontSize: '0.9rem' }}>
                Verificando progresso anterior...
              </Typography>
            </Box>
          </SectionCard>
        )}

        {/* Resume prompt: site pendente no banco */}
        {!loading && pendingSite && (
          <Fade in timeout={300}>
            <SectionCard elevation={0} sx={{ border: '1px solid rgba(124,58,237,0.2)', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box sx={{
                  width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                  background: 'rgba(124,58,237,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <RefreshCw size={20} color="#A78BFA" />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ color: '#F9FAFB', fontWeight: 600, fontSize: '1rem', mb: 0.5 }}>
                    Você tem uma criação em andamento
                  </Typography>
                  <Typography sx={{ color: '#9CA3AF', fontSize: '0.85rem', mb: 0.5 }}>
                    Site: <strong style={{ color: '#A78BFA' }}>{pendingSite.name}</strong>
                    {pendingSite.wizardData?.cnpjData?.razao_social && (
                      <> — {pendingSite.wizardData.cnpjData.razao_social}</>
                    )}
                  </Typography>
                  <Typography sx={{ color: '#6B7280', fontSize: '0.78rem', mb: 2 }}>
                    {pendingSite.customDomain
                      ? `Domínio: ${pendingSite.customDomain} — falta configurar email`
                      : pendingSite.wizardData?.customDomain
                        ? `Domínio informado: ${pendingSite.wizardData.customDomain} — falta apontar DNS`
                        : 'Falta conectar um domínio próprio'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button variant="contained" size="small" startIcon={<ArrowRight size={14} />}
                      onClick={handleResume}
                      sx={{ background: '#7C3AED', textTransform: 'none', fontWeight: 600, '&:hover': { background: '#6D28D9' } }}
                    >
                      Continuar de onde parei
                    </Button>
                    <Button variant="outlined" size="small" onClick={handleStartNew}
                      sx={{ textTransform: 'none', borderColor: 'rgba(107,114,128,0.3)', color: '#6B7280', '&:hover': { borderColor: '#6B7280', background: 'rgba(107,114,128,0.06)' } }}
                    >
                      Criar novo site
                    </Button>
                  </Box>
                </Box>
              </Box>
            </SectionCard>
          </Fade>
        )}

        {/* Main wizard flow */}
        {!loading && !pendingSite && (
          <>
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2, background: 'rgba(239,68,68,0.1)', color: '#FCA5A5', border: '1px solid rgba(239,68,68,0.2)', '& .MuiAlert-icon': { color: '#EF4444' } }}>{error}</Alert>}
        {success && !isFinalStep && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2, background: 'rgba(16,185,129,0.1)', color: '#6EE7B7', border: '1px solid rgba(16,185,129,0.2)', '& .MuiAlert-icon': { color: '#10B981' } }}>{success}</Alert>}

        <Box sx={{ mb: 4 }}>
          <Stepper activeStep={activeStep} alternativeLabel connector={<FtmConnector />}>
            {steps.map((label, i) => (
              <Step key={label + i} completed={i < activeStep}>
                <StepLabel StepIconComponent={(props) => <FtmStepIcon {...props} icon={i + 1} />}>
                  <Typography sx={{ color: i <= activeStep ? '#D1D5DB' : '#4B5563', fontSize: '0.7rem', fontWeight: i === activeStep ? 600 : 400 }}>
                    {label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* STEP 0: Token Check */}
        {activeStep === 0 && (
          <SectionCard elevation={0}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Coins size={20} color="#7C3AED" />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#F9FAFB', fontSize: '1rem' }}>Verificação de Tokens</Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#6B7280', mb: 3 }}>
              A geração de site com IA custa <strong style={{ color: '#F59E0B' }}>1 token (R$ 40,00)</strong>.
            </Typography>
            <Box sx={{
              p: 3, borderRadius: 3, textAlign: 'center', mb: 3,
              background: 'rgba(124,58,237,0.06)',
              border: '1px solid rgba(124,58,237,0.2)',
            }}>
              <Typography sx={{ color: '#6B7280', fontSize: '0.82rem', mb: 1 }}>Seu saldo atual</Typography>
              <Typography sx={{ color: userTokens >= IA_TOKEN_COST ? '#7C3AED' : '#6B7280', fontWeight: 800, fontSize: '2.5rem', lineHeight: 1 }}>{userTokens}</Typography>
              <Typography sx={{ color: '#6B7280', fontSize: '0.82rem', mt: 0.5 }}>tokens</Typography>
            </Box>
            {userTokens >= IA_TOKEN_COST ? (
              <Button fullWidth variant="contained" endIcon={<ArrowRight size={16} />} onClick={handleTokenCheck}
                sx={{ background: '#10B981', textTransform: 'none', fontWeight: 600, py: 1.5, fontSize: '1rem', '&:hover': { background: '#059669' } }}>
                Começar
              </Button>
            ) : (
              <Box sx={{ textAlign: 'center' }}>
                <Alert severity="warning" sx={{ mb: 2, background: 'rgba(245,158,11,0.08)', color: '#FCD34D', border: '1px solid rgba(245,158,11,0.15)', '& .MuiAlert-icon': { color: '#F59E0B' } }}>
                  Você precisa de pelo menos {IA_TOKEN_COST} tokens para continuar.
                </Alert>
                <Button variant="contained" startIcon={<Coins size={16} />} onClick={() => navigate('/comprar-tokens')}
                  sx={{ background: '#7C3AED', textTransform: 'none', fontWeight: 600, py: 1.2, '&:hover': { background: '#6D28D9' } }}>
                  Adicionar Tokens
                </Button>
              </Box>
            )}
          </SectionCard>
        )}

        {/* STEP 1: CNPJ */}
        {activeStep === 1 && (
          <SectionCard elevation={0}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Search size={20} color="#7C3AED" />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#F9FAFB', fontSize: '1rem' }}>Buscar Empresa pelo CNPJ</Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#6B7280', mb: 3 }}>
              Informe o CNPJ para puxar os dados automaticamente da Receita Federal
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, mb: 2 }}>
              <TextField fullWidth label="CNPJ" placeholder="00.000.000/0000-00" value={cnpjInput}
                onChange={(e) => setCnpjInput(formatCNPJ(e.target.value))}
                onKeyDown={(e) => e.key === 'Enter' && handleBuscarCNPJ()} />
              <Button variant="contained" onClick={handleBuscarCNPJ} disabled={cnpjLoading}
                sx={{ minWidth: 140, height: 56, borderRadius: 3 }}
                startIcon={cnpjLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <Search size={20} />}>
                {cnpjLoading ? 'Buscando...' : 'Buscar'}
              </Button>
            </Box>
            {cnpjData && (
              <Fade in timeout={300}>
                <Box>
                  <Box sx={{ p: 2, borderRadius: 3, background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.2)', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <CheckCircle2 size={18} color="#10B981" />
                      <Typography sx={{ color: '#10B981', fontWeight: 600, fontSize: '0.9rem' }}>Empresa encontrada</Typography>
                    </Box>
                    <Typography sx={{ color: '#F9FAFB', fontWeight: 600, fontSize: '1.05rem' }}>{cnpjData.razao_social}</Typography>
                    {cnpjData.nome_fantasia && <Typography sx={{ color: '#9CA3AF', fontSize: '0.85rem' }}>Fantasia: {cnpjData.nome_fantasia}</Typography>}
                    <Typography sx={{ color: '#9CA3AF', fontSize: '0.85rem' }}>{cnpjData.municipio}/{cnpjData.uf} — {cnpjData.cnae_fiscal_descricao}</Typography>
                  </Box>
                  <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 2 }} />
                  <Typography sx={{ color: '#D1D5DB', fontWeight: 600, mb: 1.5, fontSize: '0.9rem' }}>Confirme ou altere o telefone, WhatsApp e email:</Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, mb: 2 }}>
                    <TextField fullWidth label="Telefone" value={telefone} required onChange={(e) => setTelefone(e.target.value)} placeholder="(31) 97139-1218" />
                    <TextField fullWidth label="WhatsApp" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="(31) 97139-1218" helperText="Pode ser o mesmo do telefone" />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <TextField fullWidth label="Email" value={email} required onChange={(e) => setEmail(e.target.value)} placeholder="contato@empresa.com.br" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                    <Button startIcon={<ArrowLeft size={16} />} onClick={() => setActiveStep(0)} sx={{ textTransform: 'none', color: '#9CA3AF' }}>Voltar</Button>
                    <Button variant="contained" endIcon={<ArrowRight size={16} />} disabled={!telefone.trim() || !email.trim()}
                      onClick={() => { setError(''); setActiveStep(2); }}
                      sx={{ background: '#7C3AED', textTransform: 'none', fontWeight: 600, '&:hover': { background: '#6D28D9' } }}>
                      Próximo
                    </Button>
                  </Box>
                </Box>
              </Fade>
            )}
            {!cnpjData && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
                <Button startIcon={<ArrowLeft size={16} />} onClick={() => setActiveStep(0)} sx={{ textTransform: 'none', color: '#9CA3AF' }}>Voltar</Button>
              </Box>
            )}
          </SectionCard>
        )}

        {/* STEP 2: Generate AI + Create Site */}
        {activeStep === 2 && (
          <SectionCard elevation={0}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Sparkles size={20} color="#7C3AED" />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#F9FAFB', fontSize: '1rem' }}>Gerar Site com IA</Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#6B7280', mb: 3 }}>
              Vamos gerar os textos e criar o site automaticamente com base nos dados do CNPJ
            </Typography>

            {/* Se já gerou um site (voltou nesse step) */}
            {createdSite ? (
              <Box>
                <Box sx={{ p: 3, borderRadius: 3, background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)', textAlign: 'center', mb: 3 }}>
                  <CheckCircle2 size={32} color="#10B981" style={{ marginBottom: 8 }} />
                  <Typography sx={{ color: '#10B981', fontWeight: 700, fontSize: '1.1rem', mb: 0.5 }}>
                    Você já gerou este site
                  </Typography>
                  <Typography sx={{ color: '#9CA3AF', fontSize: '0.85rem' }}>
                    Site <strong style={{ color: '#D1D5DB' }}>{createdSite.name}</strong> já foi criado. Prossiga para conectar seu domínio.
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button variant="contained" endIcon={<ArrowRight size={16} />}
                    onClick={() => setActiveStep(3)}
                    sx={{ background: '#7C3AED', textTransform: 'none', fontWeight: 600, '&:hover': { background: '#6D28D9' } }}>
                    Conectar Domínio
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box>
                <Box sx={{ p: 2.5, borderRadius: 3, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', mb: 3 }}>
                  <Typography sx={{ color: '#F9FAFB', fontWeight: 600, fontSize: '0.95rem', mb: 2 }}>{cnpjData?.razao_social}</Typography>
                  <TextField size="small" fullWidth label="Slug do site" value={dominio}
                    onChange={(e) => setDominio(formatDomain(e.target.value))} placeholder="ex: minha-empresa"
                    helperText={dominio ? `Identificador interno: ${dominio}` : 'Usado internamente para organizar os arquivos'}
                    InputProps={{ endAdornment: <InputAdornment position="end"><Typography sx={{ color: '#6B7280', fontSize: '0.75rem' }}>{dominio.length}/30</Typography></InputAdornment> }}
                    sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, mb: 2 }}>
                    <TextField size="small" fullWidth label="Telefone" value={telefone} onChange={(e) => setTelefone(e.target.value)} placeholder="(31) 97139-1218" />
                    <TextField size="small" fullWidth label="WhatsApp" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="(31) 97139-1218" />
                  </Box>
                  <Box>
                    <TextField size="small" fullWidth label="Email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="contato@empresa.com.br" />
                  </Box>
                </Box>

                <Box sx={{ textAlign: 'center' }}>
                  {iaLoading || creating ? (
                    <Box sx={{ py: 4 }}>
                      <CircularProgress size={40} sx={{ color: '#7C3AED', mb: 2 }} />
                      <Typography sx={{ color: '#D1D5DB', fontWeight: 600, fontSize: '1rem' }}>
                        {creating ? 'Criando site...' : 'Gerando textos com IA...'}
                      </Typography>
                      <Typography sx={{ color: '#6B7280', fontSize: '0.82rem', mt: 1 }}>Isso pode levar até 30 segundos</Typography>
                    </Box>
                  ) : userTokens < IA_TOKEN_COST ? (
                    <Box>
                      <Alert severity="warning" sx={{ mb: 2, background: 'rgba(245,158,11,0.08)', color: '#FCD34D', border: '1px solid rgba(245,158,11,0.15)', '& .MuiAlert-icon': { color: '#F59E0B' } }}>
                        Você não tem tokens suficientes para gerar o site. Saldo atual: {userTokens}.
                      </Alert>
                      <Button variant="contained" startIcon={<Coins size={16} />} onClick={() => navigate('/comprar-tokens')}
                        sx={{ background: '#7C3AED', textTransform: 'none', fontWeight: 600, py: 1.2, '&:hover': { background: '#6D28D9' } }}>
                        Adicionar Tokens
                      </Button>
                    </Box>
                  ) : (
                    <Button variant="contained" size="large" startIcon={<Rocket size={20} />} onClick={handleGerarECriar}
                      sx={{ px: 5, py: 1.5, borderRadius: 3, fontSize: '1rem', background: '#7C3AED', '&:hover': { background: '#6D28D9' } }}>
                      Gerar e Criar Site (1 token)
                    </Button>
                  )}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 3 }}>
                  <Button startIcon={<ArrowLeft size={16} />} onClick={() => setActiveStep(1)} disabled={iaLoading || creating}
                    sx={{ textTransform: 'none', color: '#9CA3AF' }}>Voltar</Button>
                </Box>
              </Box>
            )}
          </SectionCard>
        )}

        {/* STEP 3: Domínio Próprio + DNS */}
        {activeStep === 3 && (
          <SectionCard elevation={0}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Globe size={20} color="#7C3AED" />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#F9FAFB', fontSize: '1rem' }}>Conectar Domínio Próprio</Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#6B7280', mb: 3 }}>
              Informe o domínio que você comprou (ex: Registro.br, Hostinger, GoDaddy) e aponte-o para nosso servidor.
            </Typography>

            {!dnsStatus?.canActivate && !createdSite?.customDomain && (
              <Box sx={{ mb: 3 }}>
                <TextField fullWidth placeholder="seudominio.com.br" value={customDomain}
                  onChange={e => setCustomDomain(e.target.value.toLowerCase().replace(/\s/g, ''))}
                  onBlur={handleSaveCustomDomain}
                  error={customDomain.trim().length > 0 && !isCustomDomainValid}
                  helperText={customDomain.trim().length > 0 && !isCustomDomainValid ? 'Formato inválido. Ex: seudominio.com.br' : ''}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Globe size={16} color="#6B7280" /></InputAdornment> }}
                  sx={{ '& .MuiFormHelperText-root': { color: '#EF4444' } }} />
              </Box>
            )}

            {isCustomDomainValid && !createdSite?.customDomain && (
              <Fade in timeout={300}>
                <Box>
                  <InstructionBox sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Server size={16} color="#A78BFA" />
                      <Typography sx={{ color: '#D1D5DB', fontWeight: 600, fontSize: '0.9rem' }}>Configure no painel DNS do seu domínio</Typography>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '80px 80px 1fr' }, gap: 2, mb: 2.5, p: 2, borderRadius: 2, background: 'rgba(0,0,0,0.2)' }}>
                      <Box>
                        <Typography sx={{ color: '#6B7280', fontSize: '0.7rem', textTransform: 'uppercase', mb: 0.5, letterSpacing: '0.05em' }}>Tipo</Typography>
                        <Chip label="A" size="small" sx={{ background: 'rgba(124,58,237,0.2)', color: '#C4B5FD', fontWeight: 700, fontSize: '0.85rem' }} />
                      </Box>
                      <Box>
                        <Typography sx={{ color: '#6B7280', fontSize: '0.7rem', textTransform: 'uppercase', mb: 0.5, letterSpacing: '0.05em' }}>Nome</Typography>
                        <Chip label={nomeRegistro} size="small" sx={{ background: 'rgba(124,58,237,0.2)', color: '#C4B5FD', fontWeight: 700, fontSize: '0.85rem' }} />
                      </Box>
                      <Box>
                        <Typography sx={{ color: '#6B7280', fontSize: '0.7rem', textTransform: 'uppercase', mb: 0.5, letterSpacing: '0.05em' }}>Valor (IP do servidor)</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label={SERVER_IP} size="small" sx={{ background: 'rgba(16,185,129,0.15)', color: '#6EE7B7', fontWeight: 700, fontSize: '0.85rem', fontFamily: 'monospace' }} />
                          <Tooltip title={copied === 'ip' ? 'Copiado!' : 'Copiar IP'}>
                            <IconButton size="small" onClick={() => handleCopy(SERVER_IP, 'ip')}>
                              {copied === 'ip' ? <Check size={14} color="#10B981" /> : <Copy size={14} color="#6B7280" />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Box>
                    <Alert severity="info" icon={<AlertTriangle size={16} />} sx={{ background: 'rgba(245,158,11,0.06)', color: '#FCD34D', border: '1px solid rgba(245,158,11,0.12)', fontSize: '0.8rem', '& .MuiAlert-icon': { color: '#F59E0B' } }}>
                      Se usar Cloudflare, desative o proxy (nuvem laranja → cinza). Coloque TTL em "Auto" ou o menor possível.
                    </Alert>
                  </InstructionBox>

                  <Box sx={{ p: 2.5, borderRadius: 3, mb: 3, background: dnsStatus?.canActivate ? 'rgba(16,185,129,0.06)' : 'rgba(255,255,255,0.02)', border: `1px solid ${dnsStatus?.canActivate ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.06)'}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {dnsStatus?.canActivate ? <Wifi size={16} color="#10B981" /> : dnsChecking ? <CircularProgress size={16} sx={{ color: '#7C3AED' }} /> : <WifiOff size={16} color="#6B7280" />}
                        <Typography sx={{ color: dnsStatus?.canActivate ? '#10B981' : '#D1D5DB', fontWeight: 600, fontSize: '0.88rem' }}>
                          {dnsChecking ? 'Verificando DNS...'
                            : dnsStatus?.canActivate ? 'DNS propagado em todos os servidores!'
                            : dnsCheckCount === 0 ? 'Aguardando verificação'
                            : dnsStatus?.propagated > 0 ? `Propagando... ${dnsStatus.propagated}/${dnsStatus.total} servidores`
                            : 'Aguardando propagação...'}
                        </Typography>
                      </Box>
                      <Chip label={customDomain} size="small" sx={{ background: 'rgba(124,58,237,0.1)', color: '#A78BFA', fontWeight: 600, fontSize: '0.75rem' }} />
                    </Box>

                    {dnsChecking && <LinearProgress sx={{ mb: 2, borderRadius: 2, height: 3, background: 'rgba(124,58,237,0.1)', '& .MuiLinearProgress-bar': { background: '#7C3AED' } }} />}

                    {/* Provider propagation grid */}
                    {dnsStatus?.providers && !dnsChecking && (
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}>
                        {dnsStatus.providers.map((p) => (
                          <Box key={p.name} sx={{
                            display: 'flex', alignItems: 'center', gap: 1, p: 1.5, borderRadius: 2,
                            background: p.ok ? 'rgba(16,185,129,0.06)' : 'rgba(255,255,255,0.02)',
                            border: `1px solid ${p.ok ? 'rgba(16,185,129,0.15)' : 'rgba(255,255,255,0.04)'}`,
                          }}>
                            {p.ok ? <CheckCircle2 size={14} color="#10B981" /> : p.pointing ? <AlertTriangle size={14} color="#F59E0B" /> : <Clock size={14} color="#6B7280" />}
                            <Box>
                              <Typography sx={{ color: p.ok ? '#6EE7B7' : '#9CA3AF', fontSize: '0.78rem', fontWeight: 600 }}>{p.name}</Typography>
                              <Typography sx={{ color: '#4B5563', fontSize: '0.68rem' }}>
                                {p.ok ? 'Propagado' : p.pointing ? 'Múltiplos IPs' : p.addresses?.length > 0 ? `IP: ${p.addresses[0]}` : 'Aguardando'}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Propagation progress bar */}
                    {dnsStatus?.providers && !dnsChecking && !dnsStatus.canActivate && dnsStatus.propagated > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography sx={{ color: '#9CA3AF', fontSize: '0.72rem' }}>Progresso da propagação</Typography>
                          <Typography sx={{ color: '#A78BFA', fontSize: '0.72rem', fontWeight: 600 }}>{dnsStatus.propagated}/{dnsStatus.total}</Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={(dnsStatus.propagated / dnsStatus.total) * 100} sx={{
                          borderRadius: 2, height: 4,
                          background: 'rgba(107,114,128,0.15)',
                          '& .MuiLinearProgress-bar': { background: dnsStatus.propagated >= 3 ? '#10B981' : '#F59E0B', borderRadius: 2 }
                        }} />
                      </Box>
                    )}

                    {dnsCheckCount > 0 && !dnsStatus?.canActivate && !dnsChecking && dnsStatus?.propagated === 0 && (
                      <Box sx={{ p: 2, borderRadius: 2, mb: 2, background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.1)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                          <Clock size={16} color="#F59E0B" style={{ marginTop: 2, flexShrink: 0 }} />
                          <Box>
                            <Typography sx={{ color: '#FCD34D', fontWeight: 600, fontSize: '0.85rem', mb: 0.5 }}>Normal — DNS leva tempo para propagar</Typography>
                            <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem', lineHeight: 1.5 }}>
                              Domínios novos costumam propagar em <strong style={{ color: '#D1D5DB' }}>5 a 30 minutos</strong>.
                              Alterações em domínios existentes podem levar até algumas horas.
                              Ative a verificação automática abaixo e aguarde tranquilamente.
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}

                    {dnsStatus?.canActivate && (
                      <Alert severity="success" icon={<CheckCircle2 size={16} />} sx={{ mb: 2, background: 'rgba(16,185,129,0.08)', color: '#6EE7B7', border: '1px solid rgba(16,185,129,0.15)', '& .MuiAlert-icon': { color: '#10B981' } }}>
                        Todos os servidores DNS confirmaram: registro A apontando para {SERVER_IP}
                      </Alert>
                    )}

                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                      {!dnsStatus?.canActivate && (
                        <>
                          <Button variant="outlined" size="small" startIcon={dnsChecking ? <CircularProgress size={14} /> : <RefreshCw size={14} />}
                            disabled={dnsChecking} onClick={() => handleCheckDns(false)}
                            sx={{ textTransform: 'none', borderColor: 'rgba(124,58,237,0.3)', color: '#A78BFA', '&:hover': { borderColor: '#7C3AED', background: 'rgba(124,58,237,0.08)' } }}>
                            Verificar Agora
                          </Button>
                          <Button variant={autoCheckEnabled ? 'contained' : 'outlined'} size="small"
                            startIcon={<RefreshCw size={14} className={autoCheckEnabled ? 'spin-slow' : ''} />}
                            onClick={toggleAutoCheck}
                            sx={{ textTransform: 'none', ...(autoCheckEnabled ? { background: 'rgba(124,58,237,0.15)', color: '#A78BFA', border: '1px solid rgba(124,58,237,0.3)', '&:hover': { background: 'rgba(124,58,237,0.25)' } } : { borderColor: 'rgba(107,114,128,0.3)', color: '#6B7280', '&:hover': { borderColor: '#6B7280', background: 'rgba(107,114,128,0.08)' } }) }}>
                            {autoCheckEnabled ? 'Verificando a cada 30s...' : 'Verificação automática'}
                          </Button>
                        </>
                      )}
                      {dnsStatus?.canActivate && (
                        <Button variant="contained" size="medium" startIcon={activating ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <CheckCircle2 size={16} />}
                          disabled={activating} onClick={handleActivateDomain}
                          sx={{ background: '#10B981', textTransform: 'none', fontWeight: 600, px: 3, py: 1, fontSize: '0.95rem', '&:hover': { background: '#059669' } }}>
                          {activating ? 'Gerando certificado SSL...' : 'Ativar Domínio + SSL'}
                        </Button>
                      )}
                    </Box>

                    {dnsCheckCount > 0 && !dnsStatus?.canActivate && (
                      <Typography sx={{ color: '#4B5563', fontSize: '0.72rem', mt: 1.5 }}>
                        {dnsCheckCount} verificaç{dnsCheckCount === 1 ? 'ão realizada' : 'ões realizadas'}
                      </Typography>
                    )}
                  </Box>

                  {activating && (
                    <Box sx={{ p: 2, borderRadius: 2, background: 'rgba(124,58,237,0.06)', border: '1px solid rgba(124,58,237,0.15)', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        <CircularProgress size={16} sx={{ color: '#7C3AED' }} />
                        <Typography sx={{ color: '#D1D5DB', fontWeight: 600, fontSize: '0.85rem' }}>Configurando domínio e SSL...</Typography>
                      </Box>
                      <Typography sx={{ color: '#6B7280', fontSize: '0.78rem' }}>Isso pode levar até 2 minutos. Não feche esta página.</Typography>
                      <LinearProgress sx={{ mt: 1.5, borderRadius: 2, height: 3, background: 'rgba(124,58,237,0.1)', '& .MuiLinearProgress-bar': { background: '#7C3AED' } }} />
                    </Box>
                  )}
                </Box>
              </Fade>
            )}

            {!isCustomDomainValid && !createdSite?.customDomain && (
              <Box sx={{ p: 2.5, borderRadius: 3, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <Typography sx={{ color: '#6B7280', fontSize: '0.85rem', mb: 1.5 }}>Ainda não tem um domínio?</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {[
                    { label: 'Registro.br', url: 'https://registro.br' },
                    { label: 'Hostinger', url: 'https://www.hostinger.com/br/registro-de-dominio' },
                    { label: 'GoDaddy', url: 'https://godaddy.com' },
                    { label: 'Hostgator', url: 'https://hostgator.com.br' },
                  ].map(p => (
                    <Button key={p.label} size="small" variant="outlined" endIcon={<ExternalLink size={12} />}
                      onClick={() => window.open(p.url, '_blank')}
                      sx={{ textTransform: 'none', fontSize: '0.78rem', borderColor: 'rgba(107,114,128,0.2)', color: '#9CA3AF', '&:hover': { borderColor: '#6B7280', background: 'rgba(107,114,128,0.06)' } }}>
                      {p.label}
                    </Button>
                  ))}
                </Box>
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 3 }}>
              <Button startIcon={<ArrowLeft size={16} />} onClick={() => setActiveStep(2)} sx={{ textTransform: 'none', color: '#9CA3AF' }}>Voltar</Button>
            </Box>
          </SectionCard>
        )}

        {/* STEP 4: MX / Email */}
        {activeStep === 4 && (
          <SectionCard elevation={0}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Mail size={20} color="#7C3AED" />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#F9FAFB', fontSize: '1rem' }}>Configurar Email (Registro MX)</Typography>
            </Box>
            <InstructionBox sx={{ mb: 3 }}>
              <Typography sx={{ color: '#D1D5DB', fontWeight: 600, mb: 2, fontSize: '0.88rem' }}>No painel DNS do seu domínio, crie o registro MX:</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '80px 80px 1fr 80px' }, gap: 2, mb: 2, p: 2, borderRadius: 2, background: 'rgba(0,0,0,0.2)' }}>
                {[
                  { label: 'Tipo', value: 'MX' },
                  { label: 'Nome', value: nomeRegistro },
                  { label: 'Valor', value: 'ftmpainel.com.br', copy: true },
                  { label: 'Prioridade', value: '10' },
                ].map(item => (
                  <Box key={item.label}>
                    <Typography sx={{ color: '#6B7280', fontSize: '0.7rem', textTransform: 'uppercase', mb: 0.5, letterSpacing: '0.05em' }}>{item.label}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label={item.value} size="small" sx={{ background: 'rgba(124,58,237,0.15)', color: '#C4B5FD', fontWeight: 700, fontSize: item.copy ? '0.8rem' : '0.85rem', fontFamily: item.copy ? 'monospace' : 'inherit' }} />
                      {item.copy && (
                        <Tooltip title={copied === 'mx' ? 'Copiado!' : 'Copiar'}>
                          <IconButton size="small" onClick={() => handleCopy('ftmpainel.com.br', 'mx')}>
                            {copied === 'mx' ? <Check size={14} color="#10B981" /> : <Copy size={14} color="#6B7280" />}
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
              {ehSubdominio && (
                <Alert severity="warning" sx={{ background: 'rgba(245,158,11,0.08)', color: '#FCD34D', border: '1px solid rgba(245,158,11,0.2)', '& .MuiAlert-icon': { color: '#F59E0B' }, fontSize: '0.82rem', mb: 1 }}>
                  Seu domínio é um subdomínio: no campo <strong>Nome</strong> use <strong>{nomeRegistro}</strong> em vez de "@", dentro da zona de <strong>{getDominioRaiz(dominioAtual)}</strong>.
                </Alert>
              )}
              <Typography sx={{ color: '#6B7280', fontSize: '0.82rem' }}>
                Seu email será: <strong style={{ color: '#A78BFA' }}>contato@{createdSite?.customDomain || customDomain}</strong>
              </Typography>
            </InstructionBox>

            <Button variant="outlined" startIcon={mxChecking ? <CircularProgress size={14} /> : <RefreshCw size={14} />}
              disabled={mxChecking} onClick={handleCheckMx}
              sx={{ textTransform: 'none', borderColor: 'rgba(124,58,237,0.3)', color: '#A78BFA', mb: 2, '&:hover': { borderColor: '#7C3AED', background: 'rgba(124,58,237,0.08)' } }}>
              Verificar MX
            </Button>

            {mxStatus && (
              <Box sx={{ mb: 2 }}>
                {mxStatus.configured ? (
                  <Box>
                    <Alert severity="success" icon={<CheckCircle2 size={18} />} sx={{ background: 'rgba(16,185,129,0.08)', color: '#6EE7B7', border: '1px solid rgba(16,185,129,0.15)', '& .MuiAlert-icon': { color: '#10B981' }, mb: 2 }}>
                      MX configurado! Aponta para ftmpainel.com.br
                    </Alert>
                    <Button variant="contained" startIcon={settingUpEmail ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <MailCheck size={14} />}
                      disabled={settingUpEmail} onClick={handleSetupEmail}
                      sx={{ background: '#10B981', textTransform: 'none', fontWeight: 600, '&:hover': { background: '#059669' } }}>
                      {settingUpEmail ? 'Ativando...' : 'Ativar Email'}
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ p: 2, borderRadius: 2, background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.1)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Clock size={16} color="#F59E0B" style={{ marginTop: 2, flexShrink: 0 }} />
                      <Box>
                        <Typography sx={{ color: '#FCD34D', fontWeight: 600, fontSize: '0.85rem', mb: 0.5 }}>MX ainda não propagado</Typography>
                        <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem' }}>Adicione o registro MX e aguarde a propagação. Pode levar alguns minutos.</Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
              <Button startIcon={<ArrowLeft size={16} />} onClick={() => setActiveStep(3)} sx={{ textTransform: 'none', color: '#9CA3AF' }}>Voltar</Button>
            </Box>
          </SectionCard>
        )}

        {/* FINAL STEP */}
        {isFinalStep && (
          <SectionCard elevation={0}>
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CheckCircle2 size={48} color="#10B981" style={{ marginBottom: 16 }} />
              <Typography variant="h5" sx={{ color: '#F9FAFB', fontWeight: 700, mb: 1 }}>Tudo pronto!</Typography>
              <Typography sx={{ color: '#6B7280', mb: 3 }}>Seu site foi criado e está no ar. Agora falta o número virtual para a verificação.</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderRadius: 2, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', width: '100%', maxWidth: 500 }}>
                  <Globe size={18} color="#7C3AED" />
                  <Box sx={{ flex: 1, textAlign: 'left' }}>
                    <Typography sx={{ color: '#6B7280', fontSize: '0.72rem', textTransform: 'uppercase' }}>Site</Typography>
                    <Typography sx={{ color: '#F9FAFB', fontWeight: 600, fontSize: '0.9rem' }}>{createdSite?.customDomain || `${siteName}.ftmpainel.com.br`}</Typography>
                  </Box>
                  <Tooltip title={copied === 'site' ? 'Copiado!' : 'Copiar'}>
                    <IconButton size="small" onClick={() => handleCopy(createdSite?.customDomain ? `https://${createdSite.customDomain}` : `https://${siteName}.ftmpainel.com.br`, 'site')}>
                      {copied === 'site' ? <Check size={14} color="#10B981" /> : <Copy size={14} color="#6B7280" />}
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderRadius: 2, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', width: '100%', maxWidth: 500 }}>
                  <Mail size={18} color="#7C3AED" />
                  <Box sx={{ flex: 1, textAlign: 'left' }}>
                    <Typography sx={{ color: '#6B7280', fontSize: '0.72rem', textTransform: 'uppercase' }}>Email para verificação</Typography>
                    <Typography sx={{ color: '#F9FAFB', fontWeight: 600, fontSize: '0.9rem' }}>{siteEmail}</Typography>
                  </Box>
                  <Tooltip title={copied === 'email' ? 'Copiado!' : 'Copiar'}>
                    <IconButton size="small" onClick={() => handleCopy(siteEmail, 'email')}>
                      {copied === 'email' ? <Check size={14} color="#10B981" /> : <Copy size={14} color="#6B7280" />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              <Button variant="contained" size="large" startIcon={<Smartphone size={18} />} onClick={() => navigate('/numero-virtual')}
                sx={{ px: 5, py: 1.5, borderRadius: 3, fontSize: '1rem', background: '#7C3AED', '&:hover': { background: '#6D28D9' } }}>
                Ir para Números Virtuais
              </Button>
              <Typography sx={{ color: '#4B5563', fontSize: '0.78rem', mt: 1.5 }}>Compre um número virtual para receber o código de verificação da Meta</Typography>
              {createdSite?._id && (
                <Button
                  variant="text"
                  size="small"
                  startIcon={<Settings size={14} />}
                  onClick={() => navigate(`/configuracoes-site/${createdSite._id}`)}
                  sx={{ mt: 3, textTransform: 'none', color: '#9CA3AF', fontSize: '0.85rem', fontWeight: 500, '&:hover': { background: 'rgba(124,58,237,0.08)', color: '#A78BFA' } }}
                >
                  Personalizar logo, redes sociais e Pixel da Meta
                </Button>
              )}
            </Box>
          </SectionCard>
        )}

        <style>{`
          @keyframes spin-slow {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .spin-slow {
            animation: spin-slow 2s linear infinite;
          }
        `}</style>
          </>
        )}
      </Box>
    </Fade>
  );
}
